import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';

export const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const NotesArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Grid = styled.dl`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  margin: 0;
  font-size: 14px;

  & > dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  & > dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const Empty = styled.div<{ $danger?: boolean }>`
  font-size: 13px;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.status.danger : theme.colors.text.muted)};
  font-style: italic;
`;

/** Плейсхолдер «номер не определён» в значении поля «Номер договора». */
export const NumberPlaceholder = styled.span`
  font-size: 13px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Ненавязчивое информационное предупреждение о дубликатах номера у того
 *  же контрагента. Не блокирует сохранение — это подсказка юристу. */
export const DuplicateWarning = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.status.warning}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.sm};

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const DuplicateList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(1.5)} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};

  & > li {
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    flex-wrap: wrap;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

export const IconDangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(1)};
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition: color 160ms ease, background 160ms ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.status.danger};
    background: ${({ theme }) => theme.colors.status.danger}14;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.status.danger};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const DocsGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const DocsGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const DocsRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

export const DocsRowMeta = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const RiskDesc = styled.div`
  font-size: 13px;
  flex: 1;
`;

export const PrecontractBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.status.warning}14;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  border-radius: ${({ theme }) => theme.radius.pill};
`;

export const ConcludedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.status.success};
  background: ${({ theme }) => theme.colors.status.success}14;
  border: 1px solid ${({ theme }) => theme.colors.status.success}55;
  border-radius: ${({ theme }) => theme.radius.pill};
`;

export const SeverityBadge = styled(Badge)<{ $sev: string }>`
  background: ${({ $sev, theme }) =>
    $sev === 'critical'
      ? theme.colors.status.danger + '22'
      : $sev === 'major'
        ? theme.colors.status.warning + '22'
        : theme.colors.surface};
  color: ${({ $sev, theme }) =>
    $sev === 'critical'
      ? theme.colors.status.danger
      : $sev === 'major'
        ? theme.colors.status.warning
        : theme.colors.text.secondary};
  font-weight: 600;
`;

export const DocBadge = styled.span`
  flex-shrink: 0;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const VersionControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const VersionGroup = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
`;

export const VersionHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background-color 160ms ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

export const VersionToggle = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  transition:
    transform 200ms ${({ theme }) => theme.motion.easeOut},
    color 160ms ease-out;
  ${({ $open, theme }) => ($open ? `color: ${theme.colors.status.success};` : '')}
`;

export const VersionLabel = styled.span`
  flex: 1;
  font-weight: 600;
  font-size: 14px;
`;

export const VersionMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const VersionContent = styled.div`
  padding: 0 ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  animation: groupReveal 220ms ${({ theme }) => theme.motion.easeOut};
  @keyframes groupReveal {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const FlexRow = styled.div<{ $gap?: number; $align?: string; $justify?: string; $wrap?: boolean }>`
  display: flex;
  align-items: ${({ $align }) => $align ?? 'center'};
  justify-content: ${({ $justify }) => $justify ?? 'flex-start'};
  gap: ${({ theme, $gap }) => theme.spacing($gap ?? 2)};
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
`;

export const FlexCol = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $gap }) => theme.spacing($gap ?? 2)};
`;

export const InlineActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const DangerButtonOutline = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 160ms ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.danger}14;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export function dt(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ru-RU') : '—';
}

export function shortDocPath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts.slice(-2).join('/');
}

export const CATEGORY_LABELS: Record<string, string> = {
  subject: 'Предмет',
  price: 'Цена',
  terms: 'Сроки',
  liability: 'Ответственность',
  warranty: 'Гарантии',
  force_majeure: 'Форс-мажор',
  jurisdiction: 'Подсудность',
};

export const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Критическое',
  major: 'Важное',
  minor: 'Незначительное',
};

export const DEADLINE_KIND_LABELS: Record<string, string> = {
  claim_response: 'Срок ответа на претензию',
  work_start: 'Срок начала работ',
  work_end: 'Срок окончания работ',
  warranty: 'Гарантийный срок',
  prolong: 'Пролонгация / расторжение',
  force_majeure_notice: 'Уведомление о форс-мажоре',
  payment: 'Срок оплаты',
  procedural: 'Процессуальный срок',
  answer: 'Срок ответа',
  statute_of_limitations: 'Срок исковой давности',
};

export const EXEC_TERM_KIND_LABELS: Record<string, string> = {
  payment_stage: 'Оплата',
  delivery: 'Поставка',
  quality_acceptance: 'Приёмка по качеству',
  work_milestone: 'Производство работ',
  notification_period: 'Уведомления',
  other: 'Прочее',
};

export const RESPONSIBLE_ROLE_LABELS: Record<string, string> = {
  accounting: 'Бухгалтерия',
  mto: 'МТО',
  pto: 'ПТО',
  field: 'Стройка',
  legal: 'Юристы',
  tender: 'Тендерный',
  other: '—',
};

export const TRIGGER_EVENT_LABELS: Record<string, string> = {
  contract_signed: 'после подписания договора',
  document_date: 'от даты документа',
  act_signed: 'после подписания акта',
  invoice_issued: 'после выставления счёта',
  delivery: 'после поставки',
  acceptance: 'после приёмки',
  other: 'по условию',
};
